import { useCallback, useEffect, useState } from "react";
import { EXECUTION_ACTIONS } from "../domain/executionReducer";
import { REST_TIMER_MAX_SECONDS, remainingRestSeconds } from "../domain/restTimer";

// §14.3/§14.5 presentation. The persisted authority is record.restTimer and
// record.restOffer; everything here is derived from them plus the wall clock
// and is never written back. Time passing (RUNNING → DONE → HIDDEN) dispatches
// nothing: no revision, activity, IndexedDB write or sync.

export const REST_DONE_DISPLAY_MS = 3000;

export const REST_DISPLAY = Object.freeze({
  HIDDEN: "HIDDEN",
  OFFER: "OFFER",
  RUNNING: "RUNNING",
  DONE: "DONE",
});

const HIDDEN = Object.freeze({
  state: REST_DISPLAY.HIDDEN,
  remainingSeconds: 0,
  durationSeconds: null,
  timer: null,
  offer: null,
  nextChangeAt: null,
});

function timestamp(value) {
  const time = new Date(value ?? Number.NaN).getTime();
  return Number.isFinite(time) ? time : null;
}

// A RUNNING or DONE timer outranks an offer. The two never coexist in state
// produced by the commit path (an anchor replaces both; Play clears the
// offer), so this precedence only keeps malformed local state renderable.
// `nextChangeAt` is the absolute time at which the derived display next
// changes (the next whole-second boundary, endsAt, or the end of DONE); null
// when nothing changes with time (OFFER, HIDDEN).
export function deriveRestDisplay(record, nowMs) {
  if (record?.document?.status !== "ACTIVE") return HIDDEN;
  const timer = record.restTimer || null;
  const endsAt = timestamp(timer?.endsAt);
  if (endsAt != null) {
    if (nowMs < endsAt) {
      return {
        state: REST_DISPLAY.RUNNING,
        remainingSeconds: remainingRestSeconds(timer, nowMs),
        durationSeconds: timer.durationSeconds,
        timer,
        offer: null,
        nextChangeAt: nowMs + ((endsAt - nowMs) % 1000 || 1000),
      };
    }
    if (nowMs < endsAt + REST_DONE_DISPLAY_MS) {
      return {
        state: REST_DISPLAY.DONE,
        remainingSeconds: 0,
        durationSeconds: timer.durationSeconds,
        timer,
        offer: null,
        nextChangeAt: endsAt + REST_DONE_DISPLAY_MS,
      };
    }
  }
  const offer = record.restOffer || null;
  if (offer && Number.isInteger(offer.durationSeconds) && offer.durationSeconds > 0) {
    return {
      state: REST_DISPLAY.OFFER,
      remainingSeconds: offer.durationSeconds,
      durationSeconds: offer.durationSeconds,
      timer: null,
      offer,
      nextChangeAt: null,
    };
  }
  return HIDDEN;
}

function currentTime() {
  return Date.now();
}

function scheduleTimeout(callback, delay) {
  return setTimeout(callback, delay);
}

function cancelTimeout(timeoutId) {
  clearTimeout(timeoutId);
}

export default function useRestTimer(record, {
  dispatch,
  // False for a read-only mirror or while the screen cannot edit: every
  // control is disabled up front and nothing is dispatched.
  canEdit = false,
  documentObject = typeof document === "undefined" ? null : document,
  now = currentTime,
  setTimeoutFn = scheduleTimeout,
  clearTimeoutFn = cancelTimeout,
} = {}) {
  const [clock, setClock] = useState(0);
  const refresh = useCallback(() => setClock((value) => value + 1), []);
  // Read the clock on every render: a new timer (Play, a new anchor, reload)
  // is always shown against the current time, never a stale tick.
  const display = deriveRestDisplay(record, now());
  const { nextChangeAt } = display;

  // One timeout to the next absolute change, re-armed after each refresh. No
  // timeout exists for OFFER, HIDDEN or a stale expired timer, and none while
  // the document is hidden; skipped ticks are never replayed.
  useEffect(() => {
    if (nextChangeAt == null || documentObject?.visibilityState === "hidden") return undefined;
    const timeoutId = setTimeoutFn(refresh, Math.max(0, nextChangeAt - now()));
    return () => clearTimeoutFn(timeoutId);
  }, [clock, clearTimeoutFn, documentObject, nextChangeAt, now, refresh, setTimeoutFn]);

  // visibilitychange → visible recomputes from the absolute endsAt at once.
  useEffect(() => {
    if (!documentObject?.addEventListener) return undefined;
    documentObject.addEventListener("visibilitychange", refresh);
    return () => documentObject.removeEventListener("visibilitychange", refresh);
  }, [documentObject, refresh]);

  const actionAt = () => new Date(now()).toISOString();
  const running = display.state === REST_DISPLAY.RUNNING;
  const offering = display.state === REST_DISPLAY.OFFER;
  const canExtend = Boolean(canEdit && running && display.durationSeconds < REST_TIMER_MAX_SECONDS);

  function play() {
    if (!canEdit || !offering) return undefined;
    // Manual Play starts from this tap, with the offer's anchor and frozen
    // duration; the reducer clears the offer in the same revision.
    return dispatch({
      type: EXECUTION_ACTIONS.START_REST_TIMER,
      source: "MANUAL",
      anchor: display.offer.anchor,
      durationSeconds: display.offer.durationSeconds,
      at: actionAt(),
    });
  }

  function extend() {
    if (!canExtend) return undefined;
    return dispatch({ type: EXECUTION_ACTIONS.EXTEND_REST_TIMER, seconds: 30, at: actionAt() });
  }

  function dismiss() {
    if (!canEdit || (!running && !offering)) return undefined;
    return dispatch({
      type: running ? EXECUTION_ACTIONS.DISMISS_REST_TIMER : EXECUTION_ACTIONS.DISMISS_REST_OFFER,
      at: actionAt(),
    });
  }

  return {
    state: display.state,
    remainingSeconds: display.remainingSeconds,
    durationSeconds: display.durationSeconds,
    canEdit: Boolean(canEdit),
    canExtend,
    play,
    extend,
    dismiss,
  };
}
